import { createContext, useCallback, useMemo, useRef, useState, type PropsWithChildren } from 'react';
import { fetchPlanPrices } from '@/services/billing.client';
import type { PlanPrices } from '@/types';

/**
 * Where the Pro price is on its way from Dodo.
 *
 * Three states and no fourth: there is no "fallback" price. A figure written
 * into the bundle would be shown as current long after the price changed in
 * Dodo, so while the answer is loading the UI shows a placeholder, and if it
 * never comes the UI says so.
 */
export type PlanPricingState =
  | { status: 'loading' }
  | { status: 'ready'; prices: PlanPrices }
  | { status: 'error' };

export interface PlanPricingContextValue {
  state: PlanPricingState;
  /** Starts the request if nothing has been fetched yet, or the last try failed. */
  load: () => void;
}

export const PlanPricingContext = createContext<PlanPricingContextValue | null>(null);

/**
 * The current Pro prices, fetched once per page load and only when needed.
 *
 * Lazy: nothing is requested until a component that shows a price mounts
 * (`usePlanPricing` calls `load`). The quote builder works offline and never
 * shows a subscription price, so it never makes the request.
 *
 * Once per page load: a successful answer is kept for the life of the page.
 * The server caches for a minute (`functions/_pricing.ts`), so a reload shows a
 * price changed in Dodo within that window. A failed answer is not kept — the
 * next price view to mount tries again.
 */
export function PlanPricingProvider({ children }: PropsWithChildren) {
  const [state, setState] = useState<PlanPricingState>({ status: 'loading' });
  const phase = useRef<'idle' | 'loading' | 'ready' | 'error'>('idle');

  const load = useCallback(() => {
    if (phase.current === 'loading' || phase.current === 'ready') return;
    phase.current = 'loading';
    setState({ status: 'loading' });
    fetchPlanPrices().then(
      (prices) => {
        phase.current = 'ready';
        setState({ status: 'ready', prices });
      },
      () => {
        phase.current = 'error';
        setState({ status: 'error' });
      },
    );
  }, []);

  const value = useMemo<PlanPricingContextValue>(() => ({ state, load }), [state, load]);
  return <PlanPricingContext.Provider value={value}>{children}</PlanPricingContext.Provider>;
}
